import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import AutoDeleteIcon from "@mui/icons-material/AutoDelete";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SettingsIcon from "@mui/icons-material/Settings";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useQuery } from "@tanstack/react-query";
import StreamEventsTimeline from "./StreamEventsTimeline";
import StreamEventFiltersDialog, {
  createDefaultStreamEventErrorFilterSettings,
  resolveStreamEventErrorFilterStorageKey,
  StreamEventErrorFilterSettings
} from "./StreamEventFiltersDialog";
import { useAppContext } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { openExternalPath } from "../utils/externalLinks";

type StreamEventsDrawerStream = {
  id: string;
  sensor: string;
};

type StreamEventsDrawerProps = {
  open: boolean;
  onClose: () => void;
  stream: StreamEventsDrawerStream | null;
};

type StreamFileSourceInfoEntry = {
  streamId: string;
  mpegTsErrorRate?: number | null;
  klvErrorRate?: number | null;
};

type StreamsFileSourceInfosResponse = {
  streams?: StreamFileSourceInfoEntry[];
  error?: string;
};

const StreamEventsDrawer = ({ open, onClose, stream }: StreamEventsDrawerProps) => {
  const { authorizedFetch, user } = useAuth();
  const { serverConfig } = useAppContext();
  const [clearSignal, setClearSignal] = useState(0);
  const [showAllSignal, setShowAllSignal] = useState(0);
  const [isDeletePending, setIsDeletePending] = useState(false);
  const [totalEvents, setTotalEvents] = useState(0);
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  const [errorFilterSettings, setErrorFilterSettings] = useState<StreamEventErrorFilterSettings>(
    createDefaultStreamEventErrorFilterSettings
  );
  const isAdminUser = (user?.role ?? "").trim().toLowerCase() === "admin";
  const filterStorageKey = resolveStreamEventErrorFilterStorageKey(user);

  useEffect(() => {
    setTotalEvents(0);
  }, [stream?.id]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      const stored = window.localStorage.getItem(filterStorageKey);
      if (!stored) {
        setErrorFilterSettings(createDefaultStreamEventErrorFilterSettings());
        return;
      }
      const parsed = JSON.parse(stored) as Record<string, unknown>;
      const next = createDefaultStreamEventErrorFilterSettings();
      Object.keys(next).forEach((key) => {
        if (typeof parsed[key] === "boolean") {
          next[key] = parsed[key] as boolean;
        }
      });
      setErrorFilterSettings(next);
    } catch {
      setErrorFilterSettings(createDefaultStreamEventErrorFilterSettings());
    }
  }, [filterStorageKey]);

  const handleClearTimeline = useCallback(() => {
    setClearSignal((current) => current + 1);
  }, []);

  const handleShowAllTimelineEvents = useCallback(() => {
    setShowAllSignal((current) => current + 1);
  }, []);

  const handleToggleErrorFilter = useCallback((errorKey: string, enabled: boolean) => {
    setErrorFilterSettings((current) => {
      const next = { ...current, [errorKey]: enabled };
      if (typeof window !== "undefined") {
        window.localStorage.setItem(filterStorageKey, JSON.stringify(next));
      }
      return next;
    });
  }, [filterStorageKey]);

  const handleOpenStreamEventsHelp = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }
    void openExternalPath("/help/user-guide/stream-events.html");
  }, []);

  const handleDeleteTimelineEventsForever = useCallback(async () => {
    if (!stream || !isAdminUser || isDeletePending) {
      return;
    }
    setIsDeletePending(true);
    try {
      const response = await authorizedFetch(`/api/streams/${encodeURIComponent(stream.id)}/events`, {
        method: "DELETE"
      });
      const payload = (await response.json().catch(() => null)) as { error?: string } | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to delete stream events from server");
      }
      setShowAllSignal((current) => current + 1);
    } catch (error) {
      if (error instanceof Error) {
        window.alert(error.message);
      } else {
        window.alert("Unable to delete stream events from server");
      }
    } finally {
      setIsDeletePending(false);
    }
  }, [authorizedFetch, isAdminUser, isDeletePending, stream]);

  const disabledErrorMessages = Object.entries(errorFilterSettings)
    .filter(([, enabled]) => !enabled)
    .map(([errorKey]) => errorKey);
  const mpegTsStatusQuery = useQuery<StreamsFileSourceInfosResponse>({
    queryKey: ["stream-events", "mpegts-error-rate", stream?.id ?? ""],
    enabled: serverConfig.errorDetectionEnabled && open && Boolean(stream?.id),
    refetchInterval: 2000,
    refetchIntervalInBackground: true,
    queryFn: async () => {
      const response = await authorizedFetch("/api/streams/file_source_infos");
      const payload = (await response.json().catch(() => null)) as StreamsFileSourceInfosResponse | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load stream stats");
      }
      return payload ?? { streams: [] };
    }
  });
  const mpegTsErrorRate = (() => {
    if (!stream) {
      return null;
    }
    const entry = (mpegTsStatusQuery.data?.streams ?? []).find((item) => item.streamId === stream.id);
    const value = Number(entry?.mpegTsErrorRate ?? Number.NaN);
    return Number.isFinite(value) ? value : null;
  })();
  const mpegTsErrorRateLabel = mpegTsErrorRate === null ? "—" : `${mpegTsErrorRate.toFixed(2)} /s`;
  const klvErrorRate = (() => {
    if (!stream) {
      return null;
    }
    const entry = (mpegTsStatusQuery.data?.streams ?? []).find((item) => item.streamId === stream.id);
    const value = Number(entry?.klvErrorRate ?? Number.NaN);
    return Number.isFinite(value) ? value : null;
  })();
  const klvErrorRateLabel = klvErrorRate === null ? "—" : `${klvErrorRate.toFixed(2)} /s`;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} keepMounted>
      <Box
        sx={{
          width: { xs: 280, sm: 320, md: 360 },
          display: "flex",
          flexDirection: "column",
          height: "100%"
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 2,
            mt: 1
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1">Stream events</Typography>
            {stream ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {stream.sensor}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Tooltip title="Config">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => setIsConfigDialogOpen(true)}
                        aria-label="Configure stream event error filters"
                      >
                        <SettingsIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Show all event">
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleShowAllTimelineEvents}
                        aria-label="Show all events"
                      >
                        <Badge badgeContent={totalEvents} color="primary" showZero max={9999}>
                          <ReceiptLongIcon fontSize="small" />
                        </Badge>
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Clear">
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleClearTimeline}
                        aria-label="Clear stream events"
                      >
                        <AutoDeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {isAdminUser ? (
                    <Tooltip title="Delete all stream events from the server">
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleDeleteTimelineEventsForever}
                          aria-label="Delete stream events forever"
                          disabled={isDeletePending}
                        >
                          {isDeletePending ? (
                            <CircularProgress size={16} thickness={6} />
                          ) : (
                            <DeleteForeverIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                  <Tooltip title="Help">
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleOpenStreamEventsHelp}
                        aria-label="Open stream events help"
                      >
                        <HelpOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            ) : null}
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close stream events timeline"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider />
        <Box sx={{ flexGrow: 1, overflowY: "auto", px: 2, py: 2 }}>
          {stream ? (
            <StreamEventsTimeline
              streamId={stream.id}
              clearSignal={clearSignal}
              showAllSignal={showAllSignal}
              onTotalEventsChange={setTotalEvents}
              disabledErrorMessages={disabledErrorMessages}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Select a stream to view its events.
            </Typography>
          )}
        </Box>
        {serverConfig.errorDetectionEnabled ? (
          <>
            <Divider />
            <Box
              sx={{
                px: 2,
                py: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1
              }}
            >
              <Typography variant="caption" color="text.secondary">
                MPEG-TS error rate:{" "}
                <Box component="span" sx={{ color: "warning.main" }}>
                  {mpegTsErrorRateLabel}
                </Box>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                KLV error rate:{" "}
                <Box component="span" sx={{ color: "warning.main" }}>
                  {klvErrorRateLabel}
                </Box>
              </Typography>
            </Box>
          </>
        ) : null}
      </Box>
      <StreamEventFiltersDialog
        open={isConfigDialogOpen}
        onClose={() => setIsConfigDialogOpen(false)}
        onOpenHelp={handleOpenStreamEventsHelp}
        errorFilterSettings={errorFilterSettings}
        onToggleErrorFilter={handleToggleErrorFilter}
      />
    </Drawer>
  );
};

export default StreamEventsDrawer;
